import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { Trade, RiskLimit } from '../types';
// @ts-ignore
import { usePortfolio } from '../../context/PortfolioContext';
// @ts-ignore
import { useAuth } from '../../context/AuthContext';
// @ts-ignore
import { getWatchlist, fetchTransactions } from '../../services/api'; 
import { api } from '../services/api'; 
import toast from 'react-hot-toast';
import { fetchBatchPrices } from '../services/MarketDataService';

// --- CONFIGURATION & TYPES ---

const INITIAL_LIMITS: RiskLimit[] = [
    { id: 'L1', entity: 'Desk: FX Options', metric: 'Delta (USD)', usage: 45000000, limit: 50000000, utilization: 90 },
    { id: 'L2', entity: 'Desk: Rates Swaps', metric: 'DV01', usage: 12000, limit: 25000, utilization: 48 },
    { id: 'L3', entity: 'CPTY: Goldman Sachs', metric: 'PFE (95%)', usage: 8500000, limit: 10000000, utilization: 85 },
    { id: 'L4', entity: 'CPTY: Citibank', metric: 'Settlement Limit', usage: 12000000, limit: 10000000, utilization: 120 }, 
];

export interface AiNotification {
    id: string;
    type: 'info' | 'warning' | 'critical' | 'success' | 'audit';
    message: string;
    timestamp: string;
    read: boolean;
    user?: string;
    module?: string;
}

interface AiQStateContextType {
  trades: Trade[];
  limits: RiskLimit[];
  notifications: AiNotification[];
  systemDate: string;
  portfolioStats: {
      totalValuation: number;
      dailyPnL: number;
      tradeCount: number;
      pnlChangeTick: number;
  };
  marketData: Record<string, number>;
  watchlist: string[];
  portfolios: any[];
  activePortfolioId: string | null;
  switchPortfolio: (id: string) => void;
  addTrade: (trade: any) => Promise<void>;
  updateTradeStatus: (id: string, status: Trade['status']) => void;
  updateMarketData: (symbol: string, price: number) => void;
  refreshRisk: () => void;
  addNotification: (type: AiNotification['type'], message: string, module?: string) => void;
  logAuditEvent: (action: string, details: string) => void;
  markAllRead: () => void;
  clearNotifications: () => void;
  runDayEndBatch: () => Promise<void>;
}

const AiQContext = createContext<AiQStateContextType | undefined>(undefined);

export const AiQProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // 1. CONSUME GLOBAL CONTEXT
  const { 
      activePortfolioId, 
      switchActivePortfolio, 
      activePortfolioSummary, 
      portfolios, 
      refreshPortfolios
  } = usePortfolio();
  
  const { currentUser } = useAuth();

  // Local State
  const [localTransactions, setLocalTransactions] = useState<any[]>([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);

  const [notifications, setNotifications] = useState<AiNotification[]>(() => {
      const saved = localStorage.getItem('aiq_notifications');
      return saved ? JSON.parse(saved) : [];
  });

  const [systemDate, setSystemDate] = useState<string>(() => {
      return localStorage.getItem('aiq_system_date') || new Date().toISOString().split('T')[0];
  });

  const [limits, setLimits] = useState<RiskLimit[]>(INITIAL_LIMITS);
  const [marketData, setMarketData] = useState<Record<string, number>>({});
  const [watchlist, setWatchlist] = useState<string[]>([]);

  // --- 2. PORTFOLIO SWITCHING LOGIC ---
  const switchPortfolio = (id: string) => {
      console.log("AiQ: Switching to Book ID:", id);
      if (switchActivePortfolio) {
          switchActivePortfolio(id); 
          addNotification('info', `Switched Analysis Context to Portfolio ID: ${id}`, 'System');
      } else {
          console.error("AiQ Critical: switchActivePortfolio is missing from context.");
          toast.error("System Error: Cannot switch books.");
      }
  };

  // --- 3. FETCH TRANSACTIONS WHEN PORTFOLIO CHANGES ---
  useEffect(() => {
      const loadData = async () => {
          // Only fetch if we have both a portfolio and a user
          if (!activePortfolioId || !currentUser) {
              // Silently clear transactions without showing errors
              setLocalTransactions([]);
              setIsLoadingTransactions(false);
              return;
          }
          
          // Only fetch if portfolio exists in the portfolios list
          const portfolioExists = portfolios && portfolios.some((p: any) => p.id === activePortfolioId);
          if (!portfolioExists) {
              console.log("AiQ: Portfolio not in list yet, skipping transaction fetch");
              setLocalTransactions([]);
              setIsLoadingTransactions(false);
              return;
          }

          console.log(`AiQ: Fetching Transactions for Book ${activePortfolioId}...`);
          setIsLoadingTransactions(true);
          try {
              const token = await currentUser.getIdToken();
              const data = await fetchTransactions(token, activePortfolioId);
              
              if (data && data.transactions) {
                  setLocalTransactions(data.transactions);
                  console.log("AiQ: Loaded", data.transactions.length, "transactions.");
              } else {
                  setLocalTransactions([]);
              }
          } catch (e: any) {
              console.error("AiQ: Failed to load transactions", e);
              // Suppress toasts for expected "no data" errors (new user, auth mismatch, etc.)
              const msg = e.message || '';
              if (!msg.includes('not found') && !msg.includes('Forbidden') && !msg.includes('Unauthorized')) {
                  toast.error(`Could not load transactions: ${e.message}`);
              }
              setLocalTransactions([]);
          } finally {
              setIsLoadingTransactions(false);
          }
      };
      loadData();
  }, [activePortfolioId, currentUser, portfolios]);

  // --- 4. WATCHLIST SYNC ---
  useEffect(() => {
      const fetchSharedWatchlist = async () => {
          if (!currentUser) return;
          try {
              const token = await currentUser.getIdToken();
              const data = await getWatchlist(token);
              if (data && data.watchlist) {
                  const tickers = data.watchlist.map((w: any) => w.ticker);
                  setWatchlist(tickers);
                  
                  const initialPrices: Record<string, number> = {};
                  data.watchlist.forEach((w: any) => {
                      if (w.price) initialPrices[w.ticker] = w.price;
                  });
                  setMarketData(prev => ({ ...prev, ...initialPrices }));
                  console.log("AiQ: Watchlist Synced", tickers);
              }
          } catch (e) {
              console.error("AiQ: Failed to sync watchlist", e);
          }
      };
      fetchSharedWatchlist();
  }, [currentUser]);

  // --- 5. DATA MAPPING (Backend -> Terminal Format) ---
  const trades: Trade[] = useMemo(() => {
      if (!localTransactions || localTransactions.length === 0) {
          return [];
      }

      return localTransactions.map((tx: any) => {
          let product = `${tx.ticker} Equity`;
          if (tx.ticker.includes('/')) product = `${tx.ticker} Spot`;
          
          const price = Number(tx.price) || 0;
          const amount = Number(tx.amount) || 0;
          const val = amount * (marketData[tx.ticker] || price);

          // Explicitly cast object to Trade or any to bypass strict checks
          const tradeObj = {
              id: tx.id?.toString() || `TX-${Math.random().toString(36).substr(2, 9)}`,
              product: product,
              counterparty: 'Exchange',
              notional: Math.abs(amount * price),
              currency: 'USD',
              valuation: val,
              status: 'Settled' as Trade['status'], // FIX: Explicit Type Cast
              tradeDate: tx.date ? new Date(tx.date).toISOString().split('T')[0] : systemDate,
              settlementDate: tx.date ? new Date(tx.date).toISOString().split('T')[0] : systemDate,
              trader: currentUser?.displayName || 'You',
              rawTicker: tx.ticker,
              
              // Extra fields for PortfolioView
              ticker: tx.ticker,
              quantity: amount,
              price: price,
              side: (tx.type || 'buy').toLowerCase() === 'buy' ? 'Buy' : 'Sell'
          };

          return tradeObj as unknown as Trade; // Double cast to ensure compatibility
      });
  }, [localTransactions, marketData, currentUser, systemDate]);

  useEffect(() => {
      const syncMarketData = async () => {
          // 1. Get list of unique tickers from current trades/book
          if (localTransactions.length === 0) return;

          const uniqueTickers = Array.from(new Set(
              localTransactions.map((tx: any) => tx.ticker).filter((t: any) => t && t !== 'USD')
          ));

          if (uniqueTickers.length === 0) return;

          console.log(`AiQ: Syncing prices for ${uniqueTickers.length} assets...`);

          try {
              if (currentUser) {
                  const token = await currentUser.getIdToken();
                  const newPrices = await fetchBatchPrices(uniqueTickers, token);
                  
                  // Update State: Merge new prices with existing cache
                  setMarketData(prev => {
                      const cleanPrices: Record<string, number> = {};
                      Object.entries(newPrices).forEach(([key, val]) => {
                          if (val !== null && val !== undefined) cleanPrices[key] = val;
                      });
                      
                      return { ...prev, ...cleanPrices };
                  });
              }
          } catch (e) {
              console.error("AiQ: Market Data Sync Failed", e);
          }
      };

      // Only sync when transactions actually change, not on every render
      if (localTransactions.length > 0) {
          const timeoutId = setTimeout(() => syncMarketData(), 500);
          return () => clearTimeout(timeoutId);
      }

  }, [localTransactions, currentUser]); // Changed from 'trades' to 'localTransactions' 

  // --- 6. STATS ENGINE ---
  const portfolioStats = useMemo(() => {
      if (activePortfolioSummary) {
          return {
              totalValuation: activePortfolioSummary.total_portfolio_value || 0,
              dailyPnL: activePortfolioSummary.total_daily_pnl || 0,
              tradeCount: localTransactions.length,
              pnlChangeTick: Math.random()
          };
      }

      let totalValuation = 0;
      trades.forEach((t: any) => totalValuation += t.valuation);

      return {
          totalValuation,
          dailyPnL: 0, 
          tradeCount: trades.length,
          pnlChangeTick: Math.random()
      };
  }, [activePortfolioSummary, trades, localTransactions]);

  // --- 7. ACTIONS & UTILS ---

  const updateMarketData = (symbol: string, price: number) => {
      setMarketData(prev => ({ ...prev, [symbol]: price }));
  };

  const recalcLimits = (currentTrades: Trade[]) => {
      setLimits(prevLimits => prevLimits.map(limit => {
          const jitter = (Math.random() - 0.5) * 100000;
          let newUsage = limit.usage + jitter;
          if (portfolioStats.totalValuation > 0 && limit.metric === 'Notional (USD)') {
               newUsage = portfolioStats.totalValuation * 0.4;
          }
          const util = (newUsage / limit.limit) * 100;
          
          if (util > 100 && limit.utilization <= 100) {
              addNotification('critical', `Limit Breach: ${limit.entity} is at ${util.toFixed(1)}%`, 'Risk Engine');
          }
          return { ...limit, usage: newUsage, utilization: util };
      }));
  };

  const addNotification = (type: AiNotification['type'], message: string, module: string = 'System') => {
      const newNote: AiNotification = {
          id: Date.now().toString(),
          type,
          message,
          timestamp: new Date().toLocaleTimeString(),
          read: false,
          user: 'System',
          module
      };
      setNotifications(prev => [newNote, ...prev].slice(0, 100)); 
  };

  const logAuditEvent = (action: string, details: string) => {
      const user = currentUser?.displayName || 'Unknown User';
      const newAudit: AiNotification = {
          id: Date.now().toString(),
          type: 'audit',
          message: `${action}: ${details}`,
          timestamp: new Date().toLocaleTimeString(),
          read: true,
          user: user,
          module: 'Compliance'
      };
      setNotifications(prev => [newAudit, ...prev].slice(0, 100));
  };

  const addTrade = async (tradeData: any) => {
      if (!activePortfolioId) {
          toast.error("No Active Portfolio Selected");
          return;
      }
      
      if (!currentUser) {
          toast.error("User not authenticated");
          return;
      }

      try {
          const token = await currentUser.getIdToken();
          
          const backendTrade = {
              ticker: tradeData.product ? tradeData.product.split(' ')[0] : tradeData.symbol, 
              action: tradeData.side ? tradeData.side.toUpperCase() : 'BUY',
              amount: Number(tradeData.quantity) || Number(tradeData.notional / tradeData.price),
              price: Number(tradeData.price),
              date: new Date().toISOString() 
          };
          
          console.log("AiQ: Sending Trade to Backend", backendTrade);

          await api.portfolio.addTrade(activePortfolioId, backendTrade, token);
          
          addNotification('success', `Trade Executed: ${backendTrade.action} ${backendTrade.amount} ${backendTrade.ticker}`, 'Operations');
          logAuditEvent('Trade Entry', `Executed ${backendTrade.ticker} @ ${backendTrade.price}`);
          
          const newData = await fetchTransactions(token, activePortfolioId);
          if (newData && newData.transactions) setLocalTransactions(newData.transactions);
          
          if (refreshPortfolios) refreshPortfolios();

      } catch (error: any) {
          console.error("Trade Execution Failed", error);
          toast.error("Execution Failed: " + error.message);
          addNotification('critical', `Trade Failed: ${error.message}`, 'OMS');
      }
  };

  const updateTradeStatus = (id: string, status: Trade['status']) => {
      addNotification('info', `Trade ${id} status updated to ${status}`, 'Operations');
      logAuditEvent('Trade Lifecycle', `Updated ${id} to ${status}`);
  };

  const refreshRisk = () => recalcLimits(trades);
  const markAllRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  const clearNotifications = () => setNotifications([]);

  const runDayEndBatch = async () => {
      const current = new Date(systemDate);
      current.setDate(current.getDate() + 1);
      const nextDate = current.toISOString().split('T')[0];
      setSystemDate(nextDate);
      addNotification('success', `EOD Batch: System rolled to ${nextDate}`, 'System');
      logAuditEvent('EOD Batch', `Rolled system date to ${nextDate}`);
  };

  return (
    <AiQContext.Provider value={{ 
        trades, 
        limits, 
        notifications, 
        portfolioStats, 
        systemDate, 
        marketData, 
        watchlist, 
        portfolios: portfolios || [], 
        activePortfolioId: activePortfolioId || null, 
        switchPortfolio, 
        addTrade, 
        updateTradeStatus, 
        updateMarketData, 
        refreshRisk, 
        addNotification, 
        logAuditEvent, 
        markAllRead, 
        clearNotifications, 
        runDayEndBatch 
    }}>
      {children}
    </AiQContext.Provider>
  );
};

export const useAiQ = () => {
  const context = useContext(AiQContext);
  if (!context) throw new Error('useAiQ must be used within an AiQProvider');
  return context;
};